import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Play, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { dbHelpers } from "@/lib/database";

// Bubble Pop Game Component
const BubblePopGame = ({ onGameEnd }) => {
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [bubbles, setBubbles] = useState([]);
  const [gameActive, setGameActive] = useState(true);

  const words = useMemo(() => ['STAR', 'MOON', 'LOVE', 'PLAY', 'JUMP', 'SING', 'DRAW', 'READ'], []);

  useEffect(() => {
    if (gameActive) {
      generateBubbles();
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      onGameEnd(score);
    }
  }, [gameActive, generateBubbles, onGameEnd, score]);

  const generateBubbles = useCallback(() => {
    const newBubbles = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      word: words[Math.floor(Math.random() * words.length)],
      x: Math.random() * 80 + 10,
      y: Math.random() * 60 + 10,
      size: Math.random() * 30 + 50
    }));
    setBubbles(newBubbles);
  }, [words]);

  const popBubble = (id) => {
    setScore(prev => prev + 10);
    setBubbles(prev => prev.filter(b => b.id !== id));

    confetti({
      particleCount: 20,
      spread: 30,
      origin: { y: 0.5 }
    });

    setTimeout(generateBubbles, 500);
  };

  return (
    <div className="relative w-full h-[500px] bg-gradient-to-b from-accent/20 to-accent/5 rounded-3xl border-2 border-border overflow-hidden">
      {/* Game Header */}
      <div className="absolute top-4 left-0 right-0 flex justify-between px-6 z-10">
        <div className="bg-white/90 backdrop-blur px-4 py-2 rounded-full font-fredoka font-bold text-xl">
          ⭐ {score}
        </div>
        <div className="bg-white/90 backdrop-blur px-4 py-2 rounded-full font-fredoka font-bold text-xl">
          ⏰ {timeLeft}s
        </div>
      </div>

      {/* Bubbles */}
      <AnimatePresence>
        {bubbles.map((bubble) => (
          <motion.button
            key={bubble.id}
            data-testid={`bubble-${bubble.id}`}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => popBubble(bubble.id)}
            style={{
              position: 'absolute',
              left: `${bubble.x}%`,
              top: `${bubble.y}%`,
              width: `${bubble.size}px`,
              height: `${bubble.size}px`
            }}
            className="bg-gradient-to-br from-accent/70 to-primary/70 backdrop-blur rounded-full flex items-center justify-center font-fredoka font-bold text-white border-2 border-white/50 shadow-lg hover:scale-110 transition-transform"
          >
            {bubble.word}
          </motion.button>
        ))}
      </AnimatePresence>

      {!gameActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-white/95 backdrop-blur flex items-center justify-center"
        >
          <div className="text-center">
            <Trophy className="w-20 h-20 text-secondary mx-auto mb-4" />
            <h3 className="font-fredoka text-4xl font-bold text-foreground mb-2">
              Game Over!
            </h3>
            <p className="font-nunito text-2xl text-muted-foreground">
              Score: {score} points
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Constellation Connect Game
const ConstellationGame = ({ onGameEnd }) => {
  const [score, setScore] = useState(0);
  const [stars, setStars] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedStar, setSelectedStar] = useState(null);

  const constellations = [
    { name: 'Orion', pattern: [[1, 2], [2, 3], [3, 4], [2, 5]] },
    { name: 'Big Dipper', pattern: [[1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7]] }
  ];
  console.log('Constellations available:', constellations.length); // Avoid unused warning

  useEffect(() => {
    generateStars();
  }, []);

  const generateStars = () => {
    const newStars = Array.from({ length: 10 }, (_, i) => ({
      id: i + 1,
      x: Math.random() * 80 + 10,
      y: Math.random() * 70 + 10
    }));
    setStars(newStars);
  };

  const handleStarClick = (star) => {
    if (!selectedStar) {
      setSelectedStar(star);
    } else {
      const newConnection = [selectedStar.id, star.id].sort((a, b) => a - b);
      const exists = connections.some(
        conn => conn[0] === newConnection[0] && conn[1] === newConnection[1]
      );

      if (!exists && selectedStar.id !== star.id) {
        setConnections([...connections, newConnection]);
        setScore(prev => prev + 15);
        toast.success('⭐ Stars connected!');
      }

      setSelectedStar(null);
    }
  };

  const handleFinish = () => {
    onGameEnd(score);
  };

  return (
    <div className="relative w-full h-[500px] bg-gradient-to-b from-indigo-900 to-purple-900 rounded-3xl border-2 border-border overflow-hidden">
      {/* Score */}
      <div className="absolute top-4 right-4 z-10">
        <div className="bg-white/90 backdrop-blur px-4 py-2 rounded-full font-fredoka font-bold text-xl">
          ⭐ {score}
        </div>
      </div>

      {/* Stars */}
      <svg className="absolute inset-0 w-full h-full">
        {connections.map((conn, i) => {
          const star1 = stars.find(s => s.id === conn[0]);
          const star2 = stars.find(s => s.id === conn[1]);
          if (!star1 || !star2) return null;

          return (
            <line
              key={i}
              x1={`${star1.x}%`}
              y1={`${star1.y}%`}
              x2={`${star2.x}%`}
              y2={`${star2.y}%`}
              stroke="white"
              strokeWidth="2"
              opacity="0.6"
            />
          );
        })}
      </svg>

      {stars.map((star) => (
        <button
          key={star.id}
          data-testid={`star-${star.id}`}
          onClick={() => handleStarClick(star)}
          style={{
            position: 'absolute',
            left: `${star.x}%`,
            top: `${star.y}%`
          }}
          className={`w-8 h-8 rounded-full transition-all ${selectedStar?.id === star.id
            ? 'bg-yellow-300 scale-150 shadow-lg shadow-yellow-300/50'
            : 'bg-white hover:scale-125'
            }`}
        />
      ))}

      {/* Finish Button */}
      <button
        data-testid="finish-constellation-button"
        onClick={handleFinish}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 px-8 py-3 bg-secondary text-secondary-foreground rounded-full font-fredoka font-bold shadow-hard hover:shadow-hard-lg transition-all"
      >
        Finish
      </button>
    </div>
  );
};

// Pocket Piano Game
const PocketPianoGame = ({ onGameEnd }) => {
  const [score, setScore] = useState(0);
  const [sequence, setSequence] = useState([]);
  const [userSequence, setUserSequence] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [level, setLevel] = useState(1);

  const notes = useMemo(() => [
    { note: 'C', color: 'bg-red-400', sound: 262 },
    { note: 'D', color: 'bg-orange-400', sound: 294 },
    { note: 'E', color: 'bg-yellow-400', sound: 330 },
    { note: 'F', color: 'bg-green-400', sound: 349 },
    { note: 'G', color: 'bg-blue-400', sound: 392 },
    { note: 'A', color: 'bg-indigo-400', sound: 440 },
    { note: 'B', color: 'bg-purple-400', sound: 494 }
  ], []);

  const playSound = useCallback((frequency) => {
    if (!window.AudioContext && !window.webkitAudioContext) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  }, []);

  const playSequence = useCallback(async (seq) => {
    setIsPlaying(true);
    for (let i = 0; i < seq.length; i++) {
      const note = notes.find(n => n.note === seq[i]);
      if (note) {
        playSound(note.sound);
        await new Promise(resolve => setTimeout(resolve, 600));
      }
    }
    setIsPlaying(false);
  }, [notes, playSound]);

  const startNewRound = useCallback(() => {
    const newNote = notes[Math.floor(Math.random() * notes.length)].note;
    setSequence(prev => {
      const nextSeq = [...prev, newNote];
      playSequence(nextSeq);
      return nextSeq;
    });
    setUserSequence([]);
  }, [notes, playSequence]);

  useEffect(() => {
    startNewRound();
  }, [startNewRound]);

  const handleNoteClick = (note) => {
    if (isPlaying) return;

    const noteObj = notes.find(n => n.note === note);
    playSound(noteObj.sound);

    const newUserSequence = [...userSequence, note];
    setUserSequence(newUserSequence);

    if (newUserSequence[newUserSequence.length - 1] !== sequence[newUserSequence.length - 1]) {
      toast.error('Wrong note! Game over.');
      onGameEnd(score);
    } else if (newUserSequence.length === sequence.length) {
      const newScore = score + 10 * level;
      setScore(newScore);
      setLevel(level + 1);
      toast.success(`Level ${level} complete! +${10 * level} stars`);
      setTimeout(startNewRound, 1000);
    }
  };

  return (
    <div className="relative w-full h-[500px] bg-gradient-to-b from-purple-100 to-pink-100 rounded-3xl border-2 border-border p-6 flex flex-col">
      {/* Score */}
      <div className="flex justify-between mb-6">
        <div className="bg-white px-4 py-2 rounded-full font-fredoka font-bold text-xl">
          Level {level}
        </div>
        <div className="bg-white px-4 py-2 rounded-full font-fredoka font-bold text-xl">
          ⭐ {score}
        </div>
      </div>

      {/* Status */}
      <div className="text-center mb-6">
        <p className="font-fredoka text-2xl text-foreground">
          {isPlaying ? '🎵 Watch & Listen...' : '🎹 Your turn!'}
        </p>
        <p className="font-nunito text-muted-foreground mt-2">
          Repeat the sequence: {userSequence.length}/{sequence.length}
        </p>
      </div>

      {/* Piano Keys */}
      <div className="flex-1 flex gap-2 justify-center items-end">
        {notes.map((note) => (
          <button
            key={note.note}
            data-testid={`piano-key-${note.note}`}
            onClick={() => handleNoteClick(note.note)}
            disabled={isPlaying}
            className={`flex-1 ${note.color} rounded-t-2xl border-2 border-border shadow-hard hover:shadow-hard-lg transition-all hover:-translate-y-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-end justify-center pb-4 font-fredoka font-bold text-2xl text-white`}
            style={{ height: '200px' }}
          >
            {note.note}
          </button>
        ))}
      </div>
    </div>
  );
};

// Main Games Page
const GamesPage = () => {
  const [selectedGame, setSelectedGame] = useState(null);
  const [highScores, setHighScores] = useState({});

  const games = useMemo(() => [
    {
      id: 'bubble-pop',
      title: 'Bubble-Book Pop',
      description: 'Pop bubbles with spelling words!',
      icon: '🫧',
      color: 'from-accent/20 to-primary/20',
      component: BubblePopGame
    },
    {
      id: 'constellation',
      title: 'Constellation Connect',
      description: 'Connect stars to make patterns!',
      icon: '⭐',
      color: 'from-indigo-200 to-purple-200',
      component: ConstellationGame
    },
    {
      id: 'pocket-piano',
      title: 'Pocket Piano',
      description: 'Remember and play the tune!',
      icon: '🎹',
      color: 'from-pink-200 to-purple-200',
      component: PocketPianoGame
    }
  ], []);

  const loadHighScores = useCallback(async () => {
    const scores = {};
    for (const game of games) {
      const highScore = await dbHelpers.getHighScore(game.id);
      scores[game.id] = highScore.score || 0;
    }
    setHighScores(scores);
  }, [games]);
  const handleGameEnd = async (score) => {
    setSelectedGame(null);

    const scoreData = {
      game_name: selectedGame.id,
      score,
      stars_earned: Math.floor(score / 100),
      created_at: new Date().toISOString()
    };

    // Save score
    await dbHelpers.saveGameScore(scoreData);

    // Save achievement
    await dbHelpers.addAchievement({
      type: 'star',
      category: 'gaming',
      count: scoreData.stars_earned,
      created_at: new Date().toISOString()
    });

    // Check if new high score
    if (score > (highScores[selectedGame.id] || 0)) {
      confetti({
        particleCount: 200,
        spread: 100,
        origin: { y: 0.5 }
      });
      toast.success('🏆 New High Score!');
      setHighScores({
        ...highScores,
        [selectedGame.id]: score
      });
    }

    setTimeout(() => {
      setSelectedGame(null);
    }, 3000);
  };

  useEffect(() => {
    if (typeof loadHighScores === 'function') {
      loadHighScores();
    }
  }, [loadHighScores]);

  if (selectedGame) {
    const GameComponent = selectedGame.component;

    return (
      <div className="min-h-screen px-6 py-8 md:px-12">
        <button
          data-testid="back-to-games-button"
          onClick={() => setSelectedGame(null)}
          className="flex items-center gap-2 mb-6 px-4 py-2 bg-white border-2 border-border rounded-full font-nunito font-semibold text-foreground hover:shadow-hard transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Games
        </button>

        <h2 className="font-fredoka text-3xl font-bold text-foreground mb-6 text-center">
          {selectedGame.title}
        </h2>

        <div className="max-w-4xl mx-auto">
          <GameComponent onGameEnd={handleGameEnd} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 py-8 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <h1 className="font-fredoka text-5xl md:text-6xl font-bold text-foreground mb-4">
          Mini-Game Arcade 🎮
        </h1>
        <p className="font-nunito text-lg text-muted-foreground">
          Play games and earn stars for your treasure shelf!
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {games.map((game, index) => (
          <motion.div
            key={game.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            data-testid={`game-card-${game.id}`}
            className={`bg-gradient-to-br ${game.color} border-2 border-border rounded-3xl shadow-hard-lg p-6 hover:shadow-hard-xl hover:-translate-y-1 transition-all cursor-pointer`}
            onClick={() => setSelectedGame(game)}
          >
            <div className="text-center mb-4">
              <span className="text-6xl mb-3 block">{game.icon}</span>
              <h3 className="font-fredoka text-2xl font-bold text-foreground mb-2">
                {game.title}
              </h3>
              <p className="font-nunito text-muted-foreground">
                {game.description}
              </p>
            </div>

            <div className="flex justify-between items-center pt-4 border-t-2 border-border/50">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-secondary" />
                <span className="font-fredoka font-bold text-foreground">
                  {highScores[game.id] || 0}
                </span>
              </div>
              <button
                data-testid={`play-${game.id}-button`}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-full font-fredoka font-semibold shadow-hard hover:scale-105 transition-transform"
              >
                <Play className="w-4 h-4" />
                Play
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default GamesPage;
